import { api } from './api-client';
import {
  ADMIN_USERS_FALLBACK_PER_PAGE,
  adminUserFilterParams,
  buildAdminUsersEmptyMessage,
  buildAdminUsersViewSearch,
  buildAdminUsersViewSummary,
  resolveAdminUsersLoadedPage,
  resolveAdminUsersViewState,
  type AdminUsersFilterKey,
} from './dashboard-admin-users-state';
import {
  renderAdminUsersEmpty,
  renderAdminUsersItems,
  renderAdminUsersLoadError,
  renderAdminUsersLoadingState,
  renderAdminUsersPagination,
} from './dashboard-admin-users-view';
import { getPageUserId, type PageUser } from './page-runtime';

let adminUsersPage = 1;
let adminUsersSearch = '';
let adminUsersSearchTimer: number | undefined;
let adminUsersFilter: AdminUsersFilterKey = 'all';
let adminUsersStateHydrated = false;
let adminUsersLoading = false;
let currentAdminUserId: number | null = null;

export function setCurrentAdminUser(user: PageUser | null | undefined): void {
  currentAdminUserId = getPageUserId(user);
}

function setAdminUsersStatus(message: string, tone: 'success' | 'error' = 'success'): void {
  const el = document.getElementById('admin-users-status');
  if (!el) return;
  const toneClass = tone === 'success'
    ? 'border-brand-800 bg-brand-950/20 text-brand-300'
    : 'border-red-800 bg-red-950/20 text-red-300';
  el.className = `mb-3 rounded border px-3 py-2 text-xs ${toneClass}`;
  el.textContent = message;
  el.style.display = '';
}

function clearAdminUsersStatus(): void {
  const el = document.getElementById('admin-users-status');
  if (!el) return;
  el.textContent = '';
  el.style.display = 'none';
}

function renderAdminUsersFilterState(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-admin-users-filter]').forEach(function(btn) {
    const active = btn.dataset.adminUsersFilter === adminUsersFilter;
    btn.classList.toggle('active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
  renderAdminUsersViewSummary();
  syncAdminUsersClearState();
}

function renderAdminUsersViewSummary(): void {
  const el = document.getElementById('admin-users-view-summary');
  if (!el) return;
  const summary = buildAdminUsersViewSummary({
    filter: adminUsersFilter,
    search: adminUsersSearch,
    page: adminUsersPage,
  });
  el.textContent = summary;
  el.style.display = summary ? '' : 'none';
}

function adminUsersHasActiveFilters(): boolean {
  return adminUsersFilter !== 'all' || adminUsersSearch.length > 0 || adminUsersPage > 1;
}

function syncAdminUsersSearchInput(): void {
  const searchInput = document.getElementById('admin-users-search') as HTMLInputElement | null;
  if (searchInput && searchInput.value !== adminUsersSearch) searchInput.value = adminUsersSearch;
}

function hydrateAdminUsersViewStateFromUrl(): void {
  if (adminUsersStateHydrated || typeof window === 'undefined') return;
  const state = resolveAdminUsersViewState(window.location.search);
  adminUsersFilter = state.filter;
  adminUsersSearch = state.search;
  adminUsersPage = state.page;
  adminUsersStateHydrated = true;
  syncAdminUsersSearchInput();
}

function syncAdminUsersViewUrl(): void {
  if (typeof window === 'undefined') return;
  const nextSearch = buildAdminUsersViewSearch(window.location.search, {
    filter: adminUsersFilter,
    search: adminUsersSearch,
    page: adminUsersPage,
  });
  const currentUrl = window.location.pathname + window.location.search + window.location.hash;
  const nextUrl = window.location.pathname + nextSearch + window.location.hash;
  if (nextUrl !== currentUrl) window.history.replaceState(null, '', nextUrl);
}

function syncAdminUsersClearState(): void {
  const button = document.getElementById('admin-users-clear') as HTMLButtonElement | null;
  if (!button) return;
  const active = adminUsersHasActiveFilters() && !adminUsersLoading;
  button.disabled = !active;
  button.setAttribute('aria-disabled', active ? 'false' : 'true');
}

function setAdminUsersControlsBusy(loading: boolean): void {
  adminUsersLoading = loading;
  document.querySelectorAll<HTMLButtonElement>('[data-admin-users-filter]').forEach(function(btn) {
    btn.disabled = loading;
    btn.setAttribute('aria-disabled', loading ? 'true' : 'false');
  });
  const searchInput = document.getElementById('admin-users-search') as HTMLInputElement | null;
  if (searchInput) {
    searchInput.disabled = loading;
    searchInput.setAttribute('aria-disabled', loading ? 'true' : 'false');
  }
  syncAdminUsersClearState();
}

function setControlBusy<T extends HTMLButtonElement | HTMLSelectElement>(control: T | undefined, busyText?: string): () => void {
  if (!control) return () => {};
  const originalDisabled = control.disabled;
  const originalAriaBusy = control.getAttribute('aria-busy');
  const originalText = control instanceof HTMLButtonElement ? control.textContent : null;
  control.disabled = true;
  control.setAttribute('aria-busy', 'true');
  if (control instanceof HTMLButtonElement && busyText) control.textContent = busyText;
  return function restoreControl(): void {
    if (!control.isConnected) return;
    control.disabled = originalDisabled;
    if (originalAriaBusy === null) {
      control.removeAttribute('aria-busy');
    } else {
      control.setAttribute('aria-busy', originalAriaBusy);
    }
    if (control instanceof HTMLButtonElement && originalText !== null) control.textContent = originalText;
  };
}

export async function loadAdminUsers(): Promise<void> {
  try {
    hydrateAdminUsersViewStateFromUrl();
    renderAdminUsersFilterState();
    setAdminUsersControlsBusy(true);
    renderAdminUsersLoadingState();
    const result = await api.admin.listUsers({
      page: adminUsersPage,
      search: adminUsersSearch.length > 0 ? adminUsersSearch : undefined,
      ...adminUserFilterParams(adminUsersFilter),
    });
    if (!result.success) {
      const message = result.error || 'Failed to load users';
      setAdminUsersStatus(message, 'error');
      setAdminUsersControlsBusy(false);
      renderAdminUsersLoadError(message);
      return;
    }
    const { items, total, page, per_page, limit, total_pages, totalPages } = result.data;
    const perPage = per_page || limit || ADMIN_USERS_FALLBACK_PER_PAGE;
    const resolvedTotalPages = total_pages || totalPages;
    const resolvedPage = resolveAdminUsersLoadedPage({
      requestedPage: page,
      total,
      perPage,
      totalPages: resolvedTotalPages,
    });
    if (resolvedPage !== page) {
      adminUsersPage = resolvedPage;
      syncAdminUsersViewUrl();
      void loadAdminUsers();
      return;
    }
    adminUsersPage = page;
    syncAdminUsersViewUrl();
    renderAdminUsersViewSummary();
    setAdminUsersControlsBusy(false);
    document.getElementById('admin-users-list')?.removeAttribute('aria-busy');
    const container = document.getElementById('admin-users-list');
    if (!container) return;
    clearAdminUsersStatus();
    if (items.length === 0) {
      const emptyMessage = buildAdminUsersEmptyMessage({ filter: adminUsersFilter, search: adminUsersSearch, page });
      container.innerHTML = renderAdminUsersEmpty(emptyMessage, adminUsersHasActiveFilters());
      renderAdminUsersPagination({ total, page, perPage, totalPages: resolvedTotalPages });
      return;
    }
    container.innerHTML = renderAdminUsersItems(items, currentAdminUserId);
    renderAdminUsersPagination({ total, page, perPage, totalPages: resolvedTotalPages });
  } catch (e) {
    console.error('Failed to load admin users:', e);
    const message = 'Network error while loading users. Please try again.';
    setAdminUsersStatus(message, 'error');
    setAdminUsersControlsBusy(false);
    renderAdminUsersLoadError(message);
  }
}

export function retryAdminUsersLoad(): void {
  clearAdminUsersStatus();
  void loadAdminUsers();
}

function clearAdminUsersViewChangeStatus(): void {
  clearAdminUsersStatus();
}

export function changeAdminUsersPage(delta: number): void {
  adminUsersPage = Math.max(1, adminUsersPage + delta);
  clearAdminUsersViewChangeStatus();
  syncAdminUsersViewUrl();
  void loadAdminUsers();
}

export async function setRole(userId: number, role: string, control?: HTMLSelectElement): Promise<void> {
  if (currentAdminUserId === userId) {
    setAdminUsersStatus('You cannot change your own role from the admin list.', 'error');
    showToast('You cannot change your own role', 'error');
    return;
  }
  if (role !== 'user' && role !== 'maintainer') {
    setAdminUsersStatus('Invalid role selected.', 'error');
    showToast('Invalid role', 'error');
    return;
  }
  const currentRole = control?.dataset.currentRole || '';
  const username = control?.dataset.username || 'this user';
  if (currentRole === role) {
    clearAdminUsersStatus();
    return;
  }
  if (!confirm(`Change ${username}'s role from ${currentRole || 'current role'} to ${role}?`)) {
    if (control && currentRole) control.value = currentRole;
    clearAdminUsersStatus();
    return;
  }
  const restore = setControlBusy(control);
  setAdminUsersStatus(`Updating ${username}'s role...`);
  try {
    const result = await api.admin.setUserRole(userId, role);
    if (!result.success) {
      setAdminUsersStatus(result.error || 'Failed to update role', 'error');
      showToast(result.error || 'Failed', 'error');
      if (control && currentRole) control.value = currentRole;
    } else {
      await loadAdminUsers();
      setAdminUsersStatus(`${username}'s role is now ${role}.`);
      showToast(`${username}'s role is now ${role}`);
    }
  } catch (e) {
    setAdminUsersStatus('Network error while updating role. Please try again.', 'error');
    showToast('Network error', 'error');
    if (control && currentRole) control.value = currentRole;
  } finally {
    restore();
  }
}

export async function toggleSuspend(userId: number, currently_suspended: boolean, control?: HTMLButtonElement): Promise<void> {
  if (currentAdminUserId === userId) {
    setAdminUsersStatus('You cannot suspend your own account.', 'error');
    showToast('You cannot suspend yourself', 'error');
    return;
  }
  const username = control?.dataset.username || 'this user';
  const action = currently_suspended ? 'unsuspend' : 'suspend';
  if (!confirm(`Are you sure you want to ${action} ${username}?`)) {
    clearAdminUsersStatus();
    return;
  }
  const restore = setControlBusy(control, currently_suspended ? 'Unsuspending...' : 'Suspending...');
  setAdminUsersStatus(`${currently_suspended ? 'Unsuspending' : 'Suspending'} ${username}...`);
  try {
    const result = await api.admin.setUserSuspended(userId, !currently_suspended);
    if (!result.success) {
      setAdminUsersStatus(result.error || 'Failed to update suspension state', 'error');
      showToast(result.error || 'Failed', 'error');
    } else {
      await loadAdminUsers();
      setAdminUsersStatus(currently_suspended ? `${username} was unsuspended.` : `${username} was suspended.`);
      showToast(currently_suspended ? `${username} was unsuspended` : `${username} was suspended`);
    }
  } catch (e) {
    setAdminUsersStatus('Network error while updating suspension state. Please try again.', 'error');
    showToast('Network error', 'error');
  } finally {
    restore();
  }
}

export function usersSearch(e: Event): void {
  const nextSearch = (e.target as HTMLInputElement).value.trim();
  if (nextSearch === adminUsersSearch) {
    syncAdminUsersClearState();
    return;
  }
  adminUsersSearch = nextSearch;
  adminUsersPage = 1;
  clearAdminUsersViewChangeStatus();
  syncAdminUsersClearState();
  if (adminUsersSearchTimer !== undefined) window.clearTimeout(adminUsersSearchTimer);
  adminUsersSearchTimer = window.setTimeout(function() {
    syncAdminUsersViewUrl();
    void loadAdminUsers();
  }, 250);
}

export function clearAdminUsersFilters(): void {
  if (!adminUsersHasActiveFilters()) {
    syncAdminUsersClearState();
    return;
  }
  adminUsersFilter = 'all';
  adminUsersSearch = '';
  adminUsersPage = 1;
  clearAdminUsersViewChangeStatus();
  if (adminUsersSearchTimer !== undefined) window.clearTimeout(adminUsersSearchTimer);
  const searchInput = document.getElementById('admin-users-search') as HTMLInputElement | null;
  if (searchInput) searchInput.value = '';
  syncAdminUsersClearState();
  syncAdminUsersViewUrl();
  void loadAdminUsers();
}

export function filterAdminUsers(filter: AdminUsersFilterKey): void {
  if (filter === adminUsersFilter) {
    renderAdminUsersFilterState();
    return;
  }
  adminUsersFilter = filter;
  adminUsersPage = 1;
  clearAdminUsersViewChangeStatus();
  syncAdminUsersClearState();
  syncAdminUsersViewUrl();
  void loadAdminUsers();
}
